import * as React from 'react';
import { createContext } from 'react';
import { useHistory } from 'react-router-dom';
// eslint-disable-next-line
// @ts-ignore
import { unflatten } from 'flatley';

import { APP_ROUTES } from '@root/appRoutes';
import { useAuth } from '../Auth';

const defaultState = {
  id: undefined,
  customer: undefined,
  provider: undefined,
  snowDepth: undefined,
  additionalServices: {
    stake: undefined,
    citySidewalkShovel: undefined,
    northWalkwayShovel: undefined,
    eastWalkwayShovel: undefined,
    southWalkwayShovel: undefined,
    westWalkwayShovel: undefined,
    citySidewalkSalt: undefined,
    northWalkwaySalt: undefined,
    eastWalkwaySalt: undefined,
    southWalkwaySalt: undefined,
    westWalkwaySalt: undefined,
  },
  address: undefined,
  cost: undefined,
  status: undefined,
  currentlyStaked: undefined,
  stripePaymentMethod: undefined,
  activityTimeLog: {
    requestedTime: undefined,
  },
};

type State = any;
type ActionTypes = 'UPDATE_SERVICE_REQUEST' | 'RESET_SERVICE_REQUEST'
type Action = {
  type: ActionTypes
  payload?: {
    [key: string]: unknown
  }
};

type Reducer = (state: State, action: Action) => State
const reducer: Reducer = (state, action) => {
  const { type, payload } = action;
  let copyOfState = { ...state };
  const unflattenedPayload = unflatten(payload);

  switch (type) {
    case 'UPDATE_SERVICE_REQUEST':
      if (unflattenedPayload) {
        copyOfState = {
          ...copyOfState,
          ...unflattenedPayload,
          additionalServices: {
            ...(copyOfState?.additionalServices || {}),
            ...(typeof unflattenedPayload?.additionalServices === 'object' ? unflattenedPayload.additionalServices : {}),
          },
          activityTimeLog: {
            ...(copyOfState?.activityTimeLog || {}),
            ...(typeof unflattenedPayload?.activityTimeLog === 'object' ? unflattenedPayload.activityTimeLog : {}),
          },
        };
      }
      break;

    case 'RESET_SERVICE_REQUEST':
      copyOfState = defaultState;
      break;

    default:
      break;
  }

  return copyOfState;
};


type ContextType = {
  serviceRequestState: State
  dispatchServiceRequestState: ({ type, payload }: Action) => void
  currentStep: number
  setCurrentStep: (stepIndex: number) => void
  stepBack: () => void
  stepForward: () => void
}
const Context = createContext<ContextType>({
  serviceRequestState: {},
  dispatchServiceRequestState: () => null,
  currentStep: 0,
  setCurrentStep: () => null,
  stepBack: () => null,
  stepForward: () => null,
});

type NewServiceRequestProviderProps = {
  maxSteps: number
  children: React.ReactNode
}
const NewServiceRequestProvider: React.FC<NewServiceRequestProviderProps> = (props) => {
  const { children, maxSteps } = props;
  const [serviceRequestState, dispatchServiceRequestState] = React.useReducer(reducer, defaultState);
  const [currentStep, setCurrentStep] = React.useState(0);
  const { user } = useAuth();
  const history = useHistory();

  const stepBack = () => {
    setCurrentStep(Math.max(0, currentStep - 1));
  };

  const stepForward = () => {
    setCurrentStep(Math.min(maxSteps, currentStep + 1));
  };

  React.useEffect(() => {
    if (currentStep > maxSteps) {
      console.error(`Attempted to increment newServiceRequestProvider step count(${currentStep}) past the max step count(${maxSteps})`);
      history.push(APP_ROUTES.authenticated.customers.home);
    }
  }, [currentStep, maxSteps, history]);

  React.useEffect(() => {
    if (user === null) {
      dispatchServiceRequestState({
        type: 'RESET_SERVICE_REQUEST',
      });
    }
  }, [user]);

  return (
    <Context.Provider
      value={{
        serviceRequestState,
        dispatchServiceRequestState,

        currentStep,
        setCurrentStep,
        stepBack,
        stepForward,
      }}
    >
      {children}
    </Context.Provider>
  );
};

const useNewServiceRequest = (): ContextType => React.useContext(Context);

export {
  NewServiceRequestProvider,
  useNewServiceRequest,
};
